import { compose, pick } from 'ramda';
import Form from 'js/components/common/Form';
import { Loader } from 'js/components/common/Loader';
import { Button } from 'js/components/common/Button';
import { Input } from 'js/components/common/Fields/Input';
import React, { useState, useMemo, useCallback } from 'react';
import { withComponent } from 'js/components/hocs/withComponent';
import { componentService } from 'js/services/component.service';
import { ErrorMessage } from 'js/components/common/ErrorMessage';
import { useComponentTranslations } from 'js/hooks/component/useComponentTranslations';

import * as hooks from './hooks';
import * as actions from './store/actions';
import { PendingLimits } from './components/PendingLimits';
import { withDynamicReducer, useDynamicReducerSelector, useDynamicReducerDispatch } from './store';

import style from './AutomaticWithdrawLimit.scss';

export const AutomaticWithdrawLimitBare = () => {
    const [error, setError] = useState('');
    const dispatch = useDynamicReducerDispatch();
    const {
        isLoading, initialLimit, config: { enabled }, initialAmountToBeLeftOnBalance
    } = useDynamicReducerSelector(pick([
        'isLoading', 'initialLimit', 'config', 'initialAmountToBeLeftOnBalance'
    ]));
    const translate = useComponentTranslations();
    const { limit, amountToBeLeftOnBalance } = hooks.useLimits({ translate });
    const onSubmit = hooks.useOnSubmit({ setError, translate });
    const validationConfig = hooks.useValidationConfig({ translate });

    const initialValues = useMemo(() => ({
        limit: limit.amount,
        amountToBeLeftOnBalance: amountToBeLeftOnBalance.amount
    }), [limit, amountToBeLeftOnBalance]);

    const onChange = useCallback((event) => {
        const { name, value } = event.target;

        dispatch(actions.setLimit({ name, value }));
    }, [dispatch]);

    hooks.useFetchComponent({ setError, translate });

    if (!enabled && !error) {
        return <Loader />;
    }

    return (
        <div className={style.container}>
            <h2 className={style.title}>{translate('title')}</h2>
            <p className={style.description}>{translate('description')}</p>

            <Form
                onSubmit={onSubmit}
                initialValues={initialValues}
                validationConfig={validationConfig}
                fieldsToReplaceComma={['limit', 'amountToBeLeftOnBalance']}
                render={({ submitting, handleSubmit, values }) => (
                    <form
                        className={style.form}
                        onSubmit={event => event.preventDefault()}
                    >
                        {error && <ErrorMessage error={error} animate={true} />}
                        <Input
                            type="text"
                            name="limit"
                            inputMode="decimal"
                            onChange={onChange}
                            disabled={isLoading}
                            hint={limit.endDate}
                            className={style.field}
                            label={translate('limit.label')}
                            currencySymbol={limit.currencyCode}
                            placeholder={translate('limit.placeholder')}
                            aria-label={translate(['limit.label', 'limit.placeholder', 'playtech.mobile.system.input.aria-label'])}
                        />
                        <Input
                            type="text"
                            inputMode="decimal"
                            onChange={onChange}
                            disabled={isLoading}
                            className={style.field}
                            name="amountToBeLeftOnBalance"
                            hint={amountToBeLeftOnBalance.endDate}
                            label={translate('amountToBeLeftOnBalance.label')}
                            currencySymbol={amountToBeLeftOnBalance.currencyCode}
                            placeholder={translate('amountToBeLeftOnBalance.placeholder')}
                            aria-label={translate([
                                'amountToBeLeftOnBalance.label',
                                'amountToBeLeftOnBalance.placeholder',
                                'playtech.mobile.system.input.aria-label'
                            ])}
                        />

                        <PendingLimits translate={translate} />

                        <div className={style.controls}>
                            <Button
                                type="button"
                                onClick={handleSubmit}
                                className={style.submit}
                                disabled={
                                    submitting || isLoading
                                    || initialLimit === values.limit
                                    && initialAmountToBeLeftOnBalance === values.amountToBeLeftOnBalance
                                }
                            >
                                {translate('submit')}
                            </Button>
                        </div>
                    </form>
                )}
            >
            </Form>
        </div>
    );
};

export const AutomaticWithdrawLimit = compose(
    withComponent({
        isForLoggedIn: true,
        tkPrefix: 'playtech.mobile.system.automaticWithdrawLimit.'
    }),
    withDynamicReducer()
)(AutomaticWithdrawLimitBare);

componentService.registerWebContentComponents({ AutomaticWithdrawLimit });
